// Wire types for /trends/* endpoints. Kept loose in Phase 0 so each phase
// can refine its tab's payload shape without churning every consumer.

// v2 canonical range vocabulary (used by Pulse). The dashboard-wide
// TimeRangeControl still ships the v1 subset because the other tabs
// haven't migrated yet — `legacy` entries here are accepted by the API
// and re-rendered as their v2 equivalent.
export type RangeKey =
  | '6m' | '1y' | '2y' | '3y' | '5y' | 'all'
  | '1mo' | '12mo' | '24mo';

// Note: this list drives the shared TimeRangeControl used by Landing and
// the non-Pulse Trends tabs. Pulse has its own chip row (RangeChips) that
// uses the same v2 vocabulary. Single unified row across the landing page.
export const RANGE_OPTIONS: { value: RangeKey; label: string }[] = [
  { value: '1mo', label: '1 mo' },
  { value: '6m', label: '6 mo' },
  { value: '1y', label: '1 y' },
  { value: '2y', label: '2 y' },
  { value: '5y', label: '5 y' },
  { value: 'all', label: 'All' },
];

export type TabKey =
  | 'pulse'
  | 'infrastructure'
  | 'cve'
  | 'sectors'
  | 'geo'
  | 'ttps'
  | 'sources'
  | 'discovery';

export const TAB_ORDER: TabKey[] = [
  'pulse',
  'infrastructure',
  'cve',
  'sources',
  'geo',
  'ttps',
  'discovery',
  'sectors',
];

export const TAB_LABELS: Record<TabKey, string> = {
  pulse: 'Pulse',
  infrastructure: 'Infrastructure',
  cve: 'CVE Timeline',
  sectors: 'Sectors',
  geo: 'Geo',
  ttps: 'TTPs',
  sources: 'Sources',
  discovery: 'Discovery',
};

export const TAB_DESCRIPTIONS: Record<TabKey, string> = {
  pulse: 'Threat activity tiles — toggle Country / Actor / Family across the chosen window.',
  infrastructure: 'ASNs, IOC half-life, infrastructure countries.',
  cve: 'Disclosure → first corpus mention → first family adoption.',
  sectors: 'Victim sector mentions by year (heuristic, v1 deferred).',
  geo: 'Actor origin × infrastructure country, year-sliced.',
  ttps: 'MITRE technique mentions per top actor over time.',
  sources: 'Vendor first-movers + echo-lag distribution.',
  discovery: 'Novel entities first seen in last 30 / 90 d.',
};

export interface TrendsEnvelope {
  built_at: string | null;
  schema_version: number;
  range: RangeKey;
  cache: 'hit' | 'miss';
  empty?: boolean;
}

export interface PulseMover {
  id: string;
  name: string;
  display_name: string;
  label: string;
  z_score: number;
  recent_total: number;
  baseline_total: number;
  monthly: Array<{ ym: string; n: number }>;
}

export interface PulseStackedSeries {
  key: string;
  label: string;
  id: string;
  label_kind: string;
}

export type PulseDimension = 'country' | 'actor' | 'family';

export interface PulseTileSub {
  top_actor?: string;
  top_actor_id?: string;
  top_family?: string;
  top_family_id?: string;
  country?: string;
  country_label?: string;
  country_flag?: string;
  country_extra?: number;
  country_badge?: string;
  country_badge_label?: string;
  country_badge_flag?: string;
}

export interface PulseTile {
  id: string;
  name: string;
  flag?: string;
  type: PulseDimension;
  total: number;
  prior_total: number;
  delta_pct: number | null;
  delta_label: string;
  series: Array<{ t: string; n: number }>;
  sub: PulseTileSub;
}

export interface CountryStackSeries {
  code: string;     // ISO-2 (e.g. "RU"), the literal "__OTHER__", or "UNK" (Unattributed)
  name: string;     // "Russia" / "Other" / "Unattributed"
  flag: string;     // "🇷🇺" or ""
  total: number;
  default_visible?: boolean;
}

export interface CountryStackPayload {
  bucket: 'monthly';
  anchor_period: string | null;
  series: CountryStackSeries[];
  rows: Array<{ t: string } & Record<string, number>>;
}

export interface PulsePayload extends TrendsEnvelope {
  // ── v2 tile-grid keys ────────────────────────────────────────────────────
  dimension?: PulseDimension;
  tiles?: PulseTile[];
  anchor_period?: string | null;
  baseline_period?: string | null;
  bucket?: 'weekly' | 'monthly' | 'quarterly' | 'yearly';

  // Landing hero — only present when dimension === 'country'.
  stacked_by_country?: CountryStackPayload | null;

  // ── v1 legacy keys (deprecated, kept while the SPA cuts over) ──────────
  movers?: PulseMover[];
  stacked?: Array<{ ym: string } & Record<string, number>>;
  stacked_series?: PulseStackedSeries[];
  anchor_month?: string | null;
  recent_months?: number;
  baseline_months?: number;
}

// Which reports feed a statistic — mirrors common.stats_scope on the server.
export type StatsScope = 'all' | 'attributed' | 'focused';

export interface InfrastructurePayload extends TrendsEnvelope {
  scope?: StatsScope;
  // false when the snapshot predates scoped stats (payload is then empty)
  scope_available?: boolean;
  asn_leaderboard: Array<{
    asn: number;
    asn_org: string;
    country: string | null;
    total: number;
    quarterly: Array<{ q: string; n: number }>;
  }>;
  ioc_half_life: Array<{
    ioc_type: string;
    median_days: number;
    sample_size: number;
  }>;
  country_counts: Record<string, number>;
}

export interface CvePayload extends TrendsEnvelope {
  rows: Array<{
    cve_id: string;
    disclosure_year: number | null;
    first_mention: string | null;
    first_adoption: string | null;
    adoption_lag_days: number | null;
    families: string[];
  }>;
}

export interface SectorsPayload extends TrendsEnvelope {
  yearly_sector_counts: Record<string, Record<string, number>>;
  heuristic?: string;
}

export interface GeoPayload extends TrendsEnvelope {
  yearly_grid: Record<string, Record<string, number>>;
}

export interface TtpsPayload extends TrendsEnvelope {
  per_actor: Array<{
    actor_id: string;
    actor_name: string;
    techniques: Array<{
      technique_id: string;
      technique_name: string;
      tactic: string;
      monthly: Array<{ ym: string; n: number }>;
    }>;
  }>;
}

export interface SourcesPayload extends TrendsEnvelope {
  vendor_leaderboard: Array<{
    organization: string;
    first_mover_count: number;
    total_posts: number;
  }>;
  echo_lag: Array<{ days_bucket: string; n: number }>;
}

export interface DiscoveryPayload extends TrendsEnvelope {
  items: Array<{
    cite_key: string;
    entity_type: string;
    value: string;
    first_seen: string;
    aliases: string[];
    evidence: string;
  }>;
}
